import { Component, OnInit, OnDestroy, HostListener, effect, signal, inject, ChangeDetectionStrategy } from '@angular/core';
import { FilterStateService } from '@app/common-codes/services/FilterStateService';
import { AppRoutes } from '../../app.routes';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { Subscription } from 'rxjs';
import { ILanguage } from '@app/common-codes/_models/ILanguageDedinition';
import { ClickOutsideDirective } from '@app/common-codes/components/directives/ClickOutSideDirective';
import { AuthentificationService } from '@app/common-codes/services/authentification.service';
import { MatwinsVariablesService } from '@app/common-codes/services/matwinsVariables.service';
import { LoaderService } from '@app/common-codes/services/loader.service';
import { environment } from '../../../environments/environment';
import { TestHubComponent } from '../test-hub/test-hub.component';

interface MenuItem {
  label: string;
  route: string;
  icon?: string;
}

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterModule,
    CommonModule,
    TranslatePipe,
    ClickOutsideDirective,
    TestHubComponent
  ]
})
export class LayoutComponent implements OnInit, OnDestroy {
  protected filterStateService = inject(FilterStateService);

  // Efface les filtres de toutes les listes de la vue d'ensemble affichee.
  clearFeatureFilters(): void {
    this.filterStateService.clearActive();
  }

  isLoading = false;
  currentLanguage = 'en';
  private langSubscription: Subscription = new Subscription();
  private firstCallTranslation = false;


  sidebarOpenState = false;
  isHoveringSidebar = false;
  hoverExpanded = false;

  get sidebarOpen(): boolean {
    return this.sidebarOpenState || this.isHoveringSidebar;
  }

  langMenuOpen = false;
  userMenuOpen = false;
  showUserTooltip = false;
  userName: string | undefined;
  isFullscreen = false;

  readonly isDebug = !environment.production;
  readonly testHubOpen = signal(false);

  // Liens vers les composants racines
  menuItems: MenuItem[] = [
    // { label: 'menu.home', route: '/home', icon: 'fas fa-home' },
    // { label: 'menu.clients', route: `/${AppRoutes.Client.LIST}`, icon: 'fas fa-users' },
    // { label: 'menu.docks', route: `/${AppRoutes.Dock.LIST}`, icon: 'fas fa-warehouse' },
    // { label: 'menu.truckers', route: `/${AppRoutes.Trucker.LIST}`, icon: 'fas fa-truck' },
    // { label: 'menu.trucker-activiities', route: `/${AppRoutes.TruckerActivity.LIST}`, icon: 'fas fa-truck' },
    { label: 'menu.visitor-feature', route: '/visitor-feature', icon: 'fas fa-users' },
    { label: 'menu.trucker-feature', route: '/trucker-feature', icon: 'fas fa-truck' },
    //{ label: 'menu.trucker-feature', route: '/trucker-feature', icon: 'fas fa-truck' },
    { label: 'menu.accounts', route: `/${AppRoutes.ManagerAccount.LIST}`, icon: 'fas fa-user-gear' },
    // { label: 'menu.employees', route: `/${AppRoutes.Employee.LIST}`, icon: 'fas fa-users' },
    { label: 'Tenant Permissions', route: `/permission/tenant`, icon: 'fas fa-scale-balanced' },
    { label: 'Manager Permissions', route: `/permission/manager`, icon: 'fas fa-scale-balanced' },
    { label: 'Override Translation', route: `/override-translation`, icon: 'fas fa-language' },
    { label: 'Scheduled Tasks', route: `/scheduled-tasks`, icon: 'fas fa-list-check' },



    //{ label: 'menu.databases', route: `/${AppRoutes.DataBase.LIST}`, icon: 'fas fa-database' },
    //{ label: 'menu.migrations', route: `/${AppRoutes.Migration.LIST}`, icon: 'fas fa-exchange-alt' },
    //{ label: 'menu.logs', route: `/${AppRoutes.Log.LIST}`, icon: 'fas fa-file-alt' }
  ];

  // Remplacement de languageNames par languages
  languages: ILanguage[] | undefined = [];

  constructor(public translationService: TranslationService,
    public authentificationService: AuthentificationService,
    public matwinsVariablesService: MatwinsVariablesService,
    public loaderService: LoaderService
  ) {
    authentificationService.signin();
    effect(() => {
      const accessToken = this.matwinsVariablesService.accessTokenChanged$();
      // Attend le compte resolu : sinon un echec consomme le drapeau et le retry ne recharge rien
      const accountReady = this.authentificationService.userVerified();
      if (!this.firstCallTranslation && accountReady && accessToken != null && accessToken != '') {
        this.firstCallTranslation = true;
        this.translationService.initializeLanguage();
      }
    });
  }

  ngOnInit() {
    // Charger les langues gérées depuis le service
    this.translationService.managedLanguages$.subscribe(languages => {
      this.languages = languages;
    });

    // S'abonner aux changements de langue
    this.langSubscription = this.translationService.currentLanguage$.subscribe(lang => {
      this.currentLanguage = lang;
    });
  }

  ngOnDestroy() {
    this.langSubscription.unsubscribe();
  }

  toggleSidebar() {
    this.sidebarOpenState = !this.sidebarOpenState;
    this.isHoveringSidebar = false;
    this.hoverExpanded = false;
  }

  onSidebarMouseEnter() {
    if (!this.sidebarOpenState) {
      this.isHoveringSidebar = true;
      this.hoverExpanded = true;
    }
  }

  onSidebarMouseLeave() {
    if (this.hoverExpanded) {
      this.isHoveringSidebar = false;
      this.hoverExpanded = false;
    }
  }

  toggleLangMenu() {
    this.langMenuOpen = !this.langMenuOpen;
  }

  toggleUserMenu() {
    this.userMenuOpen = !this.userMenuOpen;
  }

  closeUserMenu() {
    this.userMenuOpen = false;
  }

  closeLangMenu() {
    this.langMenuOpen = false;
  }

  showTooltip() {
    this.showUserTooltip = true;
  }

  hideTooltip() {
    this.showUserTooltip = false;
  }


  async changeLanguage(languageKey: string) {
    this.langMenuOpen = false;
    this.isLoading = true;

    try {
      await this.translationService.use(languageKey);
    } catch (error) {
      console.error('Erreur lors du changement de langue:', error);
    } finally {
      this.isLoading = false;
    }
  }

  // obtenir le nom de la langue courante
  getCurrentLanguageName(): string {
    const currentLang = this.languages?.find(lang => lang.key === this.currentLanguage);
    return currentLang ? currentLang.name : this.currentLanguage;
  }

  // obtenir l'emoji du drapeau (fallback si le logo n'est pas encore charge)
  getFlagEmoji(languageKey: string): string {
    const flagMap: { [key: string]: string } = {
      'en': '🇬🇧',
      'en-us': '🇺🇸',
      'en-ca': '🇨🇦',
      'fr': '🇫🇷',
      'fr-ca': '🇨🇦',
      'es': '🇪🇸'
    };
    return flagMap[languageKey] || '🌐';
  }

  toggleFullscreen() {
    if (this.isFullscreen) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen();
    }
  }

  @HostListener('document:fullscreenchange', ['$event'])
  onFullscreenChange(event: Event) {
    this.isFullscreen = !!document.fullscreenElement;
  }
}
